import { useCallback, type ReactElement } from 'react';

import { useToasts } from '../notifications/useToasts';
import { toastHub as defaultHub, type ToastHub, type ToastTone } from '../notifications/toastHub';
import { Toast } from './Toast';
import styles from './Toaster.module.css';

/**
 * The live region that renders the toast stack — docs/DESIGN.md §6.6, §7.5.
 *
 * ARIA, exactly as specified and not more:
 *   · the REGION is `aria-live="polite"` — a formatting confirmation must never interrupt a
 *     screen-reader user mid-sentence;
 *   · `role="alert"` appears on the ERROR toast only (see Toast.tsx), because an error is the
 *     one case where the interruption is the point.
 *   The region is rendered even when empty. A live region has to exist in the document
 *   BEFORE content is inserted into it, or the first announcement is silently dropped.
 *
 * Hover pauses the countdown — and so does focus. A keyboard user tabbing to the Undo button
 * inside a warning toast is reading it just as much as a mouse user hovering it, and losing
 * the toast out from under the focus ring is worse than losing it under the pointer.
 */

export interface ToastLabels {
  /** Accessible name of the region, e.g. "Notifications". */
  readonly region: string;
  /** Accessible name of each toast's close control. */
  readonly dismiss: string;
  /** The severity WORD per tone — SUCCESS / ERROR / WARNING / NOTE. */
  readonly tone: Readonly<Record<ToastTone, string>>;
}

export interface ToasterProps {
  /** Already localised by the shell; `ui/**` does not translate (ADR-0003, ADR-0018). */
  readonly labels: ToastLabels;
  /** Injectable for tests; production reads the one application hub. */
  readonly hub?: ToastHub;
}

export function Toaster({ labels, hub = defaultHub }: ToasterProps): ReactElement {
  const toasts = useToasts(hub);

  const pause = useCallback(() => {
    hub.pause();
  }, [hub]);
  const resume = useCallback(() => {
    hub.resume();
  }, [hub]);
  const dismiss = useCallback(
    (id: string) => {
      hub.dismiss(id);
    },
    [hub],
  );

  return (
    <div
      className={styles.region}
      role="region"
      aria-label={labels.region}
      aria-live="polite"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          toast={toast}
          toneLabel={labels.tone[toast.tone]}
          dismissLabel={labels.dismiss}
          onDismiss={dismiss}
        />
      ))}
    </div>
  );
}

export default Toaster;
